import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  MapPin, Plus, Loader2, Search, X, Star, Power, Edit2,
} from "lucide-react";

const TYPE_BADGE = {
  WAREHOUSE: "bg-blue-100 text-blue-700 border-blue-200",
  OFFICE: "bg-slate-100 text-slate-600 border-slate-200",
  FACTORY: "bg-amber-100 text-amber-700 border-amber-200",
  STORE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  HOME: "bg-purple-100 text-purple-700 border-purple-200",
};

const INDIAN_STATES = [
  "Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra",
  "Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal",
];

export default function PickupLocations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerType, setOwnerType] = useState("ALL");
  const [search, setSearch] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (ownerType !== "ALL") params.set("owner_type", ownerType);
      const r = await api.get(`/v2/pickup-locations/list?${params.toString()}`);
      setItems(Array.isArray(r.data) ? r.data : r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  }, [ownerType]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (i.location_name || "").toLowerCase().includes(q) ||
      (i.location_code || "").toLowerCase().includes(q) ||
      (i.pincode || "").includes(q) ||
      (i.city || "").toLowerCase().includes(q)
    );
  });

  const total = items.length;
  const active = items.filter((i) => i.is_active !== false).length;
  const byType = items.reduce((m, i) => {
    const t = i.location_type || "OTHER";
    m[t] = (m[t] || 0) + 1;
    return m;
  }, {});

  const setDefault = async (code) => {
    try {
      await api.put(`/v2/pickup-locations/${code}/set-default`);
      toast.success("Default set");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (code) => {
    if (!window.confirm("Deactivate this location?")) return;
    try {
      await api.delete(`/v2/pickup-locations/${code}`);
      toast.success("Toggled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="pickup-locations-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Pickup Locations</h1>
            <p className="text-sm text-slate-500">
              Manage warehouses, stores, offices used for pickups across customers and partners
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setPanelOpen(true); }}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="add-pickup-location-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Location
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total" value={total} />
          <Stat label="Active" value={active} color="emerald" />
          {["WAREHOUSE", "OFFICE", "STORE"].map((t) => (
            <Stat key={t} label={t} value={byType[t] || 0} />
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2">
          <select
            value={ownerType}
            onChange={(e) => setOwnerType(e.target.value)}
            className="border border-slate-300 rounded h-10 px-2 text-sm bg-white"
            data-testid="owner-type-filter"
          >
            <option value="ALL">All owners</option>
            <option value="CUSTOMER">Customer</option>
            <option value="PARTNER">Partner</option>
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, pincode, city"
              className="pl-8"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <MapPin className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">No pickup locations</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pickup-locations-table">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Code</th>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Owner</th>
                    <th className="text-center p-3">Type</th>
                    <th className="text-left p-3">City</th>
                    <th className="text-left p-3">Pincode</th>
                    <th className="text-center p-3">Default</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((l) => (
                    <tr key={l.location_code} className="hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs font-semibold">{l.location_code}</td>
                      <td className="p-3 font-medium text-slate-800">{l.location_name}</td>
                      <td className="p-3 text-xs">{l.owner_type}</td>
                      <td className="p-3 text-center">
                        <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${TYPE_BADGE[l.location_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {l.location_type}
                        </span>
                      </td>
                      <td className="p-3 text-slate-600">{l.city || "—"}</td>
                      <td className="p-3 font-mono text-xs">{l.pincode || "—"}</td>
                      <td className="p-3 text-center">
                        {l.is_default ? (
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500 inline" />
                        ) : (
                          <button onClick={() => setDefault(l.location_code)} className="text-xs text-blue-600 hover:underline" data-testid={`set-default-${l.location_code}`}>
                            Set
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${
                          l.is_active !== false
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : "bg-red-100 text-red-700 border-red-200"
                        }`}>
                          {l.is_active !== false ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { setEditing(l); setPanelOpen(true); }} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded" data-testid={`edit-${l.location_code}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => toggleActive(l.location_code)} className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded" data-testid={`toggle-active-${l.location_code}`}>
                            <Power className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {panelOpen && (
        <SlideOverForm
          editing={editing}
          onClose={() => { setPanelOpen(false); setEditing(null); }}
          onSaved={() => { setPanelOpen(false); setEditing(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, color = "slate" }) {
  const c = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    slate: "bg-white border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-xl p-3 ${c}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function SlideOverForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      owner_type: "CUSTOMER",
      owner_id: "",
      location_name: "",
      contact_person: "",
      contact_mobile: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      pincode: "",
      location_type: "WAREHOUSE",
      is_default: false,
    }
  );
  const [ownerQ, setOwnerQ] = useState("");
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(editing?.location_code || null);

  useEffect(() => {
    if (!ownerOpen || form.owner_type === "PARTNER") return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/v2/customer-profiles/search?q=${encodeURIComponent(ownerQ)}`);
        setOwnerOptions(r.data?.results || []);
      } catch { setOwnerOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [ownerQ, ownerOpen, form.owner_type]);

  const submit = async () => {
    if (!form.owner_id || !form.location_name || !form.pincode || !form.city) {
      toast.error("Owner, name, city and pincode required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/v2/pickup-locations/${editing.location_code}`, form);
        toast.success("Location updated");
      } else {
        const r = await api.post(`/v2/pickup-locations/create`, form);
        setCode(r.data?.location_code || null);
        toast.success(`Created with code ${r.data?.location_code}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white shadow-2xl overflow-y-auto h-full p-5 space-y-3 animate-in slide-in-from-right duration-200"
        data-testid="pickup-location-slideover"
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">
            {editing ? "Edit Location" : "Add Pickup Location"}
          </h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {code && !editing && (
          <div className="bg-emerald-50 border border-emerald-300 rounded p-2.5 text-xs text-emerald-800">
            Created — code: <span className="font-mono font-bold">{code}</span>
          </div>
        )}

        <Field label="Owner Type">
          <div className="flex">
            {["CUSTOMER", "PARTNER"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm({ ...form, owner_type: t, owner_id: "" })}
                className={`flex-1 py-2 border text-xs font-semibold first:rounded-l last:rounded-r ${form.owner_type === t ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300"}`}
                data-testid={`owner-type-${t.toLowerCase()}`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>

        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Owner</label>
          <div className="relative">
            <Input
              value={ownerQ}
              onFocus={() => setOwnerOpen(true)}
              onChange={(e) => { setOwnerQ(e.target.value); setOwnerOpen(true); }}
              placeholder={form.owner_type === "CUSTOMER" ? "Search customer…" : "Enter partner ID"}
              data-testid="owner-search"
            />
            {ownerOpen && ownerOptions.length > 0 && form.owner_type === "CUSTOMER" && (
              <div className="absolute z-10 top-11 left-0 right-0 max-h-60 overflow-auto bg-white border border-slate-200 rounded shadow-lg">
                {ownerOptions.map((c) => (
                  <button
                    key={c.customer_id}
                    onClick={() => { setForm({ ...form, owner_id: c.customer_id }); setOwnerQ(c.company_name || c.contact_person); setOwnerOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                  >
                    <div className="font-semibold">{c.company_name || c.contact_person}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {form.owner_id && (
            <p className="text-[11px] text-emerald-600 mt-1 font-mono">ID: {form.owner_id}</p>
          )}
        </div>

        <Field label="Location Name">
          <Input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} data-testid="name-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Person">
            <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="contact-person-input" />
          </Field>
          <Field label="Contact Mobile">
            <Input value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} data-testid="contact-mobile-input" />
          </Field>
        </div>
        <Field label="Address Line 1">
          <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="address1-input" />
        </Field>
        <Field label="Address Line 2">
          <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} data-testid="address2-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City">
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="city-input" />
          </Field>
          <Field label="Pincode">
            <Input maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} data-testid="pincode-input" />
          </Field>
          <Field label="State">
            <select
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
              data-testid="state-select"
            >
              <option value="">Select…</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Location Type">
            <select
              value={form.location_type}
              onChange={(e) => setForm({ ...form, location_type: e.target.value })}
              className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
              data-testid="type-select"
            >
              {["WAREHOUSE", "OFFICE", "FACTORY", "STORE", "HOME"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            data-testid="is-default-toggle"
          />
          <span className="text-sm">Set as default for this owner</span>
        </label>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="submit-pickup-location">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {editing ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}
